/**
 * Self-contained i18n for the admin login page.
 * Intentionally NOT a full i18n library — the dashboard is English-only except
 * the login screen. Structured so it can seed a wider solution later.
 *
 * Usage:
 *   const { t, lang, setLang, isRtl } = useLoginLang();
 *   t('email.label')  // → 'Email Address' | 'البريد الإلكتروني'
 */

import { useState, useEffect } from 'react';

const LANG_KEY = 'adminLang';

// ── String maps ────────────────────────────────────────────────────────────────

const strings = {
  en: {
    // Brand panel
    'brand.product': 'She & She',
    'brand.subtitle': 'Admin Console',
    'brand.descriptor': 'Manage rides, drivers, and operations across Qatar.',

    // Form heading
    'form.heading': 'Sign in',
    'form.subheading': 'Welcome back — enter your admin credentials.',

    // Fields
    'email.label': 'Email address',
    'email.placeholder': 'admin@sheandshe.com',
    'password.label': 'Password',
    'password.placeholder': '••••••••',
    'password.show': 'Show password',
    'password.hide': 'Hide password',

    // Remember-me
    'remember.label': 'Keep me signed in',

    // Submit
    'submit.label': 'Sign in',
    'submit.loading': 'Signing in…',

    // Forgot password (informational)
    'forgot.text': 'Forgot your password? Contact your system administrator.',

    // Lang toggle
    'lang.switch': 'العربية',

    // Error messages by HTTP status
    'error.401': 'Invalid email or password.',
    'error.403': 'Your account is inactive. Contact your administrator.',
    'error.429': 'Too many login attempts. Please wait 15 minutes and try again.',
    'error.400': 'Please enter a valid email and password.',
    'error.fallback': 'Sign-in failed. Please try again.',

    // Dismiss error
    'error.dismiss': 'Dismiss',
  },

  ar: {
    // Brand panel
    'brand.product': 'فيرست ليمو',
    'brand.subtitle': 'لوحة تحكم المسؤول',
    'brand.descriptor': 'إدارة الرحلات والسائقين والعمليات في قطر.',

    // Form heading
    'form.heading': 'تسجيل الدخول',
    'form.subheading': 'مرحباً بعودتك — أدخل بيانات حساب المسؤول.',

    // Fields
    'email.label': 'البريد الإلكتروني',
    'email.placeholder': 'admin@sheandshe.com',
    'password.label': 'كلمة المرور',
    'password.placeholder': '••••••••',
    'password.show': 'إظهار كلمة المرور',
    'password.hide': 'إخفاء كلمة المرور',

    // Remember-me
    'remember.label': 'إبقائي مسجّل الدخول',

    // Submit
    'submit.label': 'دخول',
    'submit.loading': 'جارٍ الدخول…',

    // Forgot password
    'forgot.text': 'نسيت كلمة المرور؟ تواصل مع مسؤول النظام.',

    // Lang toggle
    'lang.switch': 'English',

    // Error messages by HTTP status
    'error.401': 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    'error.403': 'حسابك غير نشط. تواصل مع المسؤول.',
    'error.429': 'محاولات تسجيل دخول كثيرة. انتظر 15 دقيقة وحاول مجدداً.',
    'error.400': 'يرجى إدخال بريد إلكتروني وكلمة مرور صالحَين.',
    'error.fallback': 'فشل تسجيل الدخول. حاول مجدداً.',

    // Dismiss error
    'error.dismiss': 'إغلاق',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Map an HTTP status code to a localized error string. */
export const localizeError = (status, serverMessage, lang = 'en') => {
  const map = strings[lang] || strings.en;
  if (status === 401) return map['error.401'];
  if (status === 403) return map['error.403'];
  if (status === 429) return map['error.429'];
  if (status === 400) return map['error.400'];
  // Surface the server message as a last resort so we don't swallow useful info.
  return serverMessage || map['error.fallback'];
};

/**
 * Apply `dir` and `lang` to the document root so browser built-ins
 * (autocomplete, form validation) respect the active language.
 * Call `resetDir()` on component unmount to restore the LTR dashboard.
 */
export const applyDir = (lang) => {
  const isRtl = lang === 'ar';
  document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', lang);
};

export const resetDir = () => {
  document.documentElement.setAttribute('dir', 'ltr');
  document.documentElement.setAttribute('lang', 'en');
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useLoginLang = () => {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(LANG_KEY);
    return saved === 'ar' ? 'ar' : 'en';
  });

  const isRtl = lang === 'ar';

  // Sync dir/lang attributes whenever the language changes.
  useEffect(() => {
    applyDir(lang);
    return () => resetDir(); // restore LTR when the login page unmounts
  }, [lang]);

  const setLang = (next) => {
    localStorage.setItem(LANG_KEY, next);
    setLangState(next);
  };

  const t = (key) => {
    const map = strings[lang] || strings.en;
    return map[key] ?? strings.en[key] ?? key;
  };

  return { t, lang, setLang, isRtl };
};
